import {
  buildCoacheeAvailabilityQuery,
  CoacheeElementTokenProvider,
  CronofyDateTimePicker,
  formatCoachingTime,
} from '@efdigital/ll-addon-coaching';
import type { CoachingConfig } from '@efdigital/ll-addon-data';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  DateTimePickerAction,
  DateTimePickerConfig,
} from 'cronofy-elements';

export const MAX_SESSION_BOOKING_NOTES_LENGTH = 1000;

type SessionBookingStep = 'notes' | 'date';

function durationSelected(selected: number, minutes: number): boolean {
  return selected === minutes;
}

interface SessionBookingFlowSignature {
  Args: {
    coachId: string;
    coachingConfig: CoachingConfig;
    onSlotSelected: (
      start: string,
      end: string,
      notes: string | undefined
    ) => Promise<void>;
    sessionLengthMins: number;
    allowedDurations?: number[];
  };
}

const dateTimePickerConfig: DateTimePickerConfig = {
  logs: 'info',
};

export default class SessionBookingFlow extends Component<SessionBookingFlowSignature> {
  @tracked notes = '';
  @tracked step: SessionBookingStep = 'notes';
  @tracked selectedDurationOverride: number | null = null;

  get selectingDate(): boolean {
    return this.step === 'date';
  }

  get selectedDurationMinutes(): number {
    return this.selectedDurationOverride ?? this.args.sessionLengthMins;
  }

  get durationOptions(): number[] {
    return [...(this.args.allowedDurations ?? [])].sort((a, b) => a - b);
  }

  get showDurationPicker(): boolean {
    return this.durationOptions.length > 1;
  }

  setSessionDuration = (event: Event) => {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    if (Number.isFinite(value)) {
      this.selectedDurationOverride = value;
    }
  };

  continueToDatePicker = (event: Event) => {
    event.preventDefault();
    this.step = 'date';
  };

  editNotes = (event: Event) => {
    event.preventDefault();
    this.step = 'notes';
  };

  updateNotes = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.notes = target.value;
  };

  dateTimePickerAction = async (action: DateTimePickerAction) => {
    if (action.notification.type === 'slot_selected') {
      const { start, end } = action.notification.slot;
      const notes = this.notes.trim() || undefined;
      await this.args.onSlotSelected(start, end, notes);
    }
  };

  <template>
    <CoacheeElementTokenProvider @coachId={{@coachId}} as |cronofyContext|>
      {{#if cronofyContext.accountStatus.accountSub}}
        <div
          id="session-booking-notes"
          class="mb-4 {{if this.selectingDate 'd-none'}}"
        >
          {{#if this.showDurationPicker}}
            <div class="mb-4">
              <label class="form-label" for="session-booking-duration">
                Session length
              </label>
              <select
                class="form-select"
                id="session-booking-duration"
                data-test-session-booking-duration
                {{on "change" this.setSessionDuration}}
              >
                {{#each this.durationOptions as |mins|}}
                  <option
                    value={{mins}}
                    selected={{durationSelected
                      this.selectedDurationMinutes
                      mins
                    }}
                  >{{formatCoachingTime mins}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
          <h3 class="h4 mb-2">Comments (optional)</h3>
          <label class="form-label" for="session-booking-notes-textarea">
            Share anything you'd like your coach to know about this session.
          </label>
          <textarea
            id="session-booking-notes-textarea"
            class="form-control"
            rows="5"
            maxlength={{MAX_SESSION_BOOKING_NOTES_LENGTH}}
            value={{this.notes}}
            data-test-session-booking-notes
            {{on "input" this.updateNotes}}
          ></textarea>
          <button
            type="button"
            class="btn btn-primary mt-3"
            data-test-session-booking-notes-continue
            {{on "click" this.continueToDatePicker}}
          >
            Continue
            <iconify-icon icon="ph:arrow-right" inline aria-hidden="true" />
          </button>
        </div>

        <div
          id="session-booking-date"
          class="{{unless this.selectingDate 'd-none'}}"
        >

          <div class="hstack mb-4 align-items-start">
            <div class="flex-grow-1">

              <h3 class="h4 mb-2">Session date and time</h3>
              <p class="form-label mb-0">
                Select an available time below. When you are happy with your
                choice, confirm it to complete your booking.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-outline-primary my-0"
              {{on "click" this.editNotes}}
            >
              <iconify-icon icon="ph:arrow-left" inline aria-hidden="true" />
              {{#if this.notes}}
                Edit notes
              {{else}}
                Add notes
              {{/if}}
            </button>
          </div>

          <CronofyDateTimePicker
            @cronofyContext={{cronofyContext}}
            @availabilityQuery={{buildCoacheeAvailabilityQuery
              (hash
                coachSub=cronofyContext.accountStatus.accountSub
                durationMinutes=this.selectedDurationMinutes
                coachingConfig=@coachingConfig
              )
            }}
            @onAction={{this.dateTimePickerAction}}
            @elementConfig={{dateTimePickerConfig}}
            class="mb-5 d-flex flex-row justify-content-center"
          />
        </div>
      {{else}}
        <div class="alert alert-danger" role="alert">
          <p class="mb-0">You cannot book a session with this coach because they
            have not connected their calendar to the system.</p>
        </div>
      {{/if}}
    </CoacheeElementTokenProvider>
  </template>
}
